import { create } from "zustand";
import { toast } from "react-toastify";
import { GetCircle } from "../../../../Apis/organizationApi";
import axiosInstance from "../../../../../Hooks/axoisInstance";
import { API_BASE_URL } from "../../../../Services/baseApiUrl";

const getFileNameFromUrl = (url) => {
  try {
    const fileName = new URL(url).pathname.split("/").pop();
    return fileName || "complaint-image.jpg";
  } catch {
    return "complaint-image.jpg";
  }
};

const getImageFileFromUrl = async (imageUrl) => {
  if (!imageUrl) return null;

  const createFileFromBlob = (blob) => {
    if (!blob?.type?.startsWith("image/")) return null;
    return new File([blob], getFileNameFromUrl(imageUrl), { type: blob.type });
  };

  try {
    const response = await fetch(imageUrl, { mode: "cors" });
    if (response.ok) {
      const file = createFileFromBlob(await response.blob());
      if (file) return file;
    }
  } catch (error) {
    console.error("Fetch existing complaint image failed:", error);
  }

  try {
    const response = await axiosInstance.get(imageUrl, { responseType: "blob" });
    return createFileFromBlob(response.data);
  } catch (error) {
    console.error("Axios fetch existing complaint image failed:", error);
    return null;
  }
};

export const useDrainSlitComplaintReportStore = create((set, get) => ({
  wards: [],
  isLoadingWards: false,
  viewComplaintDetailsModal: false,
  selectedComplaint: null,
  isUpdatingComplaint: false,

  setViewComplaintDetailsModal: (value) =>
    set({
      viewComplaintDetailsModal: value,
      ...(value ? {} : { selectedComplaint: null }),
    }),

  openEditComplaint: (row) =>
    set({
      selectedComplaint: row,
      viewComplaintDetailsModal: true,
    }),

  AssignComplaintDetailsModal: false,
  isAssigningComplaint: false,

  setAssignComplaintDetailsModal: (value) =>
    set({
      AssignComplaintDetailsModal: value,
      ...(value ? {} : { selectedComplaint: null }),
    }),

  openAssignComplaint: (row) =>
    set({
      selectedComplaint: row,
      AssignComplaintDetailsModal: true,
    }),

  fetchWards: async () => {
    set({ isLoadingWards: true });
    try {
      const response = await GetCircle();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      set({
        wards: list.filter((ward) => ward.isActive !== false),
        isLoadingWards: false,
      });
    } catch (error) {
      console.error("Failed to load drain slit complaint wards:", error);
      set({ wards: [], isLoadingWards: false });
    }
  },

  complaintStatuses: [],
  fetchComplaintStatuses: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}GetComplaintStatus`);
      set({ complaintStatuses: response.data?.data || [] });
    } catch (error) {
      console.error("Failed to load complaint statuses:", error);
      set({ complaintStatuses: [] });
    }
  },

  // outer report
  drainSlitComplaintOuterReport: [],
  isLoadingDrainSlitComplaintOuterReport: false,
  filters: {
    WardId: "",
    Status: "",
    FromDate: "",
    ToDate: "",
  },
  pageNumber: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 1,

  fetchDrainSlitComplaintOuterReport: async (payload = {}) => {
    const { pageNumber, pageSize, filters } = get();
    const params = {
      WardId: payload.WardId ?? filters.WardId ?? "",
      Status: payload.Status ?? filters.Status ?? "",
      FromDate: payload.FromDate ?? filters.FromDate ?? "",
      ToDate: payload.ToDate ?? filters.ToDate ?? "",
      PageNumber: payload.PageNumber ?? pageNumber,
      PageSize: payload.PageSize ?? pageSize,
    };

    set({
      isLoadingDrainSlitComplaintOuterReport: true,
      filters: {
        WardId: params.WardId,
        Status: params.Status,
        FromDate: params.FromDate,
        ToDate: params.ToDate,
      },
      pageNumber: params.PageNumber,
      pageSize: params.PageSize,
    });

    try {
      const response = await axiosInstance.get(`${API_BASE_URL}GetDrainSlitComplaints`, { params });
      const res = response.data;

      if (res?.statusCode && res.statusCode !== 200) {
        toast.error(res.message || "Failed to load complaint report.");
        set({
          drainSlitComplaintOuterReport: [],
          totalRecords: 0,
          totalPages: 1,
        });
        return;
      }

      set({
        drainSlitComplaintOuterReport: Array.isArray(res?.data) ? res.data : [],
        pageNumber: res?.pageNumber || params.PageNumber,
        pageSize: res?.pageSize || params.PageSize,
        totalRecords: res?.totalRecords || 0,
        totalPages: res?.totalPages || 1,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load complaint report.");
      set({
        drainSlitComplaintOuterReport: [],
        totalRecords: 0,
        totalPages: 1,
      });
    } finally {
      set({ isLoadingDrainSlitComplaintOuterReport: false });
    }
  },

  // inner report (history)
  complaintHistory: {},
  isLoadingComplaintHistory: false,

  fetchDrainSlitComplaintHistory: async (complaintId) => {
    set({ isLoadingComplaintHistory: true });
    try {
      const response = await axiosInstance.get(`${API_BASE_URL}GetDrainSlitComplaintHistory`, {
        params: { ComplaintId: complaintId },
      });
      set((state) => ({
        complaintHistory: {
          ...state.complaintHistory,
          [complaintId]: response.data?.data || [],
        },
      }));
    } catch (error) {
      console.error("Failed to load complaint history:", error);
      set((state) => ({
        complaintHistory: {
          ...state.complaintHistory,
          [complaintId]: [],
        },
      }));
    } finally {
      set({ isLoadingComplaintHistory: false });
    }
  },

  updateDrainSiltComplaint: async (values) => {
    set({ isUpdatingComplaint: true });
    try {
      const formData = new FormData();
      formData.append("ComplaintNo", values.complaintNo || "");
      formData.append("RevisedWardId", values.revisedWardId || "");
      formData.append("RevisedColonyId", values.colonyId || "");
      formData.append("Status", values.status || "");
      formData.append("ActionRemarks", values.actionRemarks || "");
      formData.append("Address", values.address || "");
      formData.append("Latitude", values.latitude || "");
      formData.append("Longitude", values.longitude || "");

      let fileToUpload = null;
      if (values.uploadFile instanceof File) {
        fileToUpload = values.uploadFile;
      } else if (values.imageUrl) {
        fileToUpload = await getImageFileFromUrl(values.imageUrl);
      }

      if (fileToUpload) {
        formData.append("File", fileToUpload, fileToUpload.name);
      }

      const response = await axiosInstance.post(
        `${API_BASE_URL}UpdateDrainSiltComplaint`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      toast.success(response?.data?.message || "Complaint updated successfully.");
      set({ viewComplaintDetailsModal: false, selectedComplaint: null });
      await get().fetchDrainSlitComplaintOuterReport();
      return true;
    } catch (error) {
      console.error("Failed to update drain silt complaint:", error);
      toast.error(error?.response?.data?.message || "Failed to update complaint.");
      return false;
    } finally {
      set({ isUpdatingComplaint: false });
    }
  },

  assignDrainSiltComplaint: async (values) => {
    set({ isAssigningComplaint: true });
    try {
      const response = await axiosInstance.post(`${API_BASE_URL}AssignDrainSiltComplaint`, {
        complaintNo: values.complaintNo,
        assignedVehicleId: Number(values.assignedVehicleId) || 0,
      });

      toast.success(response?.data?.message || "Complaint assigned successfully.");
      set({ AssignComplaintDetailsModal: false, selectedComplaint: null });
      await get().fetchDrainSlitComplaintOuterReport();
      return true;
    } catch (error) {
      console.error("Failed to assign drain silt complaint:", error);
      toast.error(error?.response?.data?.message || "Failed to assign complaint.");
      return false;
    } finally {
      set({ isAssigningComplaint: false });
    }
  },
}));
